import {
  OkResponse,
  TRASH_TYPE_LABELS,
  TrashEmptyResult,
  type TrashItem,
  type TrashType,
  TrashType as TrashTypeSchema,
  formatDate,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Modal } from '../../../components/Overlays';
import { EmptyState, ErrorBox, Skeleton } from '../../../components/States';
import { useToast } from '../../../components/Toast';
import { api } from '../../../lib/api';
import { trashPath, useInvalidateAll, useTrash } from '../../../lib/data-api';
import { errorMessage } from '../../../lib/messages';
import { useIsAdmin } from '../../../lib/session';

type Confirm = { kind: 'forever'; item: TrashItem } | { kind: 'empty' } | null;

/** "Lixeira" (board Dados-Lixeira). */
export function TrashTab() {
  const isAdmin = useIsAdmin();
  const toast = useToast();
  const invalidate = useInvalidateAll();
  const trash = useTrash();
  const [type, setType] = useState<TrashType | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const restore = useMutation({
    mutationFn: (i: TrashItem) => api.post(`${trashPath(i.type, i.id)}/restore`, OkResponse),
    onSuccess: (_r, i) => {
      invalidate();
      toast(`${i.name} restaurado`);
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const forever = useMutation({
    mutationFn: (i: TrashItem) => api.delete(trashPath(i.type, i.id), TrashEmptyResult),
    onSuccess: (r) => {
      invalidate();
      toast(
        r.kept
          ? 'Ainda é usado por outros registros: fica guardado, fora da lixeira'
          : 'Excluído de vez',
      );
      setConfirm(null);
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const empty = useMutation({
    mutationFn: () => api.delete('/trash', TrashEmptyResult),
    onSuccess: (r) => {
      invalidate();
      toast(
        r.kept
          ? `Lixeira esvaziada · ${r.kept} ${r.kept === 1 ? 'item ficou guardado' : 'itens ficaram guardados'} por ainda serem usados`
          : 'Lixeira esvaziada',
      );
      setConfirm(null);
    },
    onError: (err) => toast(errorMessage(err)),
  });

  if (trash.isPending) return <Skeleton rows={3} height={48} />;
  if (trash.isError)
    return (
      <div className="card">
        <ErrorBox
          title="Não foi possível carregar a lixeira"
          onRetry={() => void trash.refetch()}
        />
      </div>
    );
  const all = trash.data.data;
  if (all.length === 0)
    return (
      <div className="card">
        <EmptyState
          title="Lixeira vazia"
          action={
            <Link className="btn" to="/configuracoes/dados">
              Voltar aos backups
            </Link>
          }
        >
          O que for excluído fica aqui por 30 dias antes de sumir de vez.
        </EmptyState>
      </div>
    );
  const rows = type ? all.filter((i) => i.type === type) : all;
  const counts = trash.data.counts;
  const types = TrashTypeSchema.options.filter((t) => (counts[t] ?? 0) > 0);
  return (
    <section className="card flat">
      <div className="row between wrap" style={{ padding: '16px 18px 6px', gap: 10 }}>
        <div>
          <h2>Lixeira</h2>
          <p className="xs muted">guarda por 30 dias</p>
        </div>
        {isAdmin ? (
          <button type="button" className="btn d sm" onClick={() => setConfirm({ kind: 'empty' })}>
            Esvaziar
          </button>
        ) : (
          <span className="sm muted">Só Admin restaura ou exclui</span>
        )}
      </div>
      {types.length > 1 ? (
        <div
          className="row wrap"
          style={{ gap: 6, padding: '6px 18px 10px' }}
          role="group"
          aria-label="Tipo"
        >
          <button
            type="button"
            className={type === null ? 'chip on' : 'chip'}
            aria-pressed={type === null}
            onClick={() => setType(null)}
          >
            Tudo <span className="xs">{all.length}</span>
          </button>
          {types.map((t) => (
            <button
              key={t}
              type="button"
              className={type === t ? 'chip on' : 'chip'}
              aria-pressed={type === t}
              onClick={() => setType(type === t ? null : t)}
            >
              {TRASH_TYPE_LABELS[t]} <span className="xs">{counts[t]}</span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="gt">
        <div
          className="gtr head"
          style={{ gridTemplateColumns: isAdmin ? '1fr 90px 220px' : '1fr 90px' }}
        >
          <span>Item</span>
          <span>Some em</span>
          {isAdmin ? <span /> : null}
        </div>
        {rows.map((i) => (
          <div
            key={`${i.type}-${i.id}`}
            className="gtr"
            style={{ gridTemplateColumns: isAdmin ? '1fr 90px 220px' : '1fr 90px' }}
          >
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 500 }}>{i.name}</span>
              <span className="xs muted">
                {[
                  i.detail,
                  `excluído${i.deletedBy ? ` por ${i.deletedBy}` : ''} · ${formatDate(i.deletedAt).slice(0, 5)}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
            <span
              className="sm"
              style={i.daysLeft <= 7 ? { color: 'var(--warn)', fontWeight: 500 } : undefined}
            >
              {i.daysLeft} {i.daysLeft === 1 ? 'dia' : 'dias'}
            </span>
            {isAdmin ? (
              <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn sm g"
                  style={{ color: 'var(--err)' }}
                  onClick={() => setConfirm({ kind: 'forever', item: i })}
                >
                  Excluir de vez
                </button>
                <button
                  type="button"
                  className="btn sm"
                  disabled={restore.isPending}
                  onClick={() => restore.mutate(i)}
                >
                  Restaurar
                </button>
              </span>
            ) : null}
          </div>
        ))}
      </div>
      {confirm?.kind === 'forever' ? (
        <Modal
          title="Excluir de vez?"
          onClose={() => setConfirm(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirm(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn d"
                disabled={forever.isPending}
                onClick={() => forever.mutate(confirm.item)}
              >
                Excluir de vez
              </button>
            </>
          }
        >
          <p>
            “{confirm.item.name}” some para sempre, sem como restaurar. Backups antigos ainda podem
            ter uma cópia.
          </p>
          <p className="xs muted">
            Se ainda for usado por pedidos, carretéis ou orçamentos antigos, fica guardado para o
            histórico e só sai da lixeira.
          </p>
        </Modal>
      ) : null}
      {confirm?.kind === 'empty' ? (
        <Modal
          title="Esvaziar a lixeira?"
          onClose={() => setConfirm(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirm(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn d"
                disabled={empty.isPending}
                onClick={() => empty.mutate()}
              >
                Esvaziar
              </button>
            </>
          }
        >
          <p>Os {all.length} itens somem para sempre, sem como restaurar.</p>
        </Modal>
      ) : null}
    </section>
  );
}
